import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Package, DollarSign, Hash } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import apiClient from '@/services/api';
import type { MenuBreakdownReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Menu Usage: every sold item ranked by revenue, with share of total. */
export default function MenuUsageReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<MenuBreakdownReport>({
    queryKey: ['menu-breakdown', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getMenuBreakdown(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const totalRev = Number(data?.totals?.revenue ?? 0);
  const items = data?.items ?? [];
  const share = (rev: number) => (totalRev > 0 ? `${((rev / totalRev) * 100).toFixed(1)}%` : '—');
  const chart = items.slice(0, 15).map((i) => ({ name: i.name, revenue: Number(i.revenue) }));
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] => [
    {
      heading: 'Item Usage',
      headers: ['Item', 'Category', 'Group', 'Qty', 'Revenue', 'Share'],
      rows: items.map((i) => [i.name, i.category, i.group, Number(i.quantity).toLocaleString(), formatCurrency(i.revenue), share(Number(i.revenue))]),
    },
  ];
  const kpis = (): [string, string][] => [
    ['Items Sold', Number(data?.totals?.quantity ?? 0).toLocaleString()],
    ['Revenue', formatCurrency(totalRev)],
    ['Distinct Items', Number(data?.totals?.distinctItems ?? 0).toLocaleString()],
  ];

  return (
    <ReportFrame
      title="Menu Usage"
      description="Every sold menu item ranked by revenue, with share of total."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={items.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`menu-usage-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Menu Usage', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Menu Usage Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label="Items Sold" value={Number(data?.totals?.quantity ?? 0).toLocaleString()} icon={Package} palette="blue" />
        <KPICard label="Revenue" value={formatCurrency(totalRev)} icon={DollarSign} palette="green" />
        <KPICard label="Distinct Items" value={Number(data?.totals?.distinctItems ?? 0).toLocaleString()} icon={Hash} palette="purple" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Top 15 by Revenue</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chart} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" width={140} />
            <Tooltip />
            <Bar dataKey="revenue" name="Revenue" fill="#0088FE" />
          </BarChart>
        </ResponsiveContainer>
      </div></div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">All Items ({items.length})</h3>
        <SimpleTable headers={['Item', 'Category', 'Group', 'Qty', 'Revenue', 'Share']}
          rows={items.map((i) => [i.name, i.category, i.group, Number(i.quantity).toLocaleString(), formatCurrency(i.revenue), share(Number(i.revenue))])} />
      </div></div>
    </ReportFrame>
  );
}
